import React from "react";
import { Add } from "@mui/icons-material";
import { Box, Button, Divider, Tab, Tabs } from "@mui/material";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";

import FormDrawer from "components/Shared/FormDrawer";
import DocumentList from "./DocumentList";
import DocumentForm from "./DocumentForm";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import { useAuth, useTablePagination } from "hooks";

import {
  useAddDocumentSourceMutation,
  useGetDocumentsBySourceQuery,
  useUpdateDocumentByIdMutation,
} from "store/services/documentApi";

const DocumentsTab = ({ source, sourceRef, isActive }) => {
  const { isCustomer, isVendor, isContractor, isDriver } = useAuth();
  const canEdit = isCustomer || isVendor || isContractor || isDriver;

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [showDocumentForm, setShowDocumentForm] = React.useState(false);
  const [selectedDocument, setSelectedDocument] = React.useState(null);
  const [searchParams, setSearchParams] = useSearchParams();


  // Use a different parameter name to avoid conflicts
  const activeDocumentTab = searchParams.get("docTab") || "Active";
  const isActiveTab = activeDocumentTab === "Active";

  // Handle tab change for document tabs specifically
  const handleDocumentTabChange = (event, newValue) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("docTab", newValue);
    setSearchParams(newParams);
  };

  const { data: documents, isLoading: loadingDocuments } =
    useGetDocumentsBySourceQuery({
      source,
      sourceRef,
      active: isActiveTab ? "true" : "false"
    });

  const [addDocumentSource, { isLoading: addingDoc }] =
    useAddDocumentSourceMutation();

  const [updateDocumentById, { isLoading: updatingDocument }] =
    useUpdateDocumentByIdMutation();

  //methods
  const handleCancelDocument = () => {
    setShowDocumentForm(false);
    setSelectedDocument(null);
  };

  const handleAddDocument = async (data) => {
    try {
      await addDocumentSource({ ...data, source, sourceRef }).unwrap();
      toast.success("Document successfully added!");
      handleCancelDocument();
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't add document!");
    }
  };

  const handleEditDocument = (document) => {
    setShowDocumentForm(true);
    setSelectedDocument(document);
  };



  const handleUpdateDocument = async (data) => {
    const body = {
      _id: selectedDocument?._id,
      title: data.title,
      attachments: data.attachments,
      source,
      vendorDocumentType: data.vendorDocumentType,
      documentType: data.documentType,
      sourceRef,
      isActive: data.isActive,
      expiryDate: data.hasExpiry ? data.expiryDate : null,
    };
    try {
      await updateDocumentById({ body }).unwrap();
      toast.success("Document successfully updated!");
      handleCancelDocument();
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update document!");
    }
  };  

  return (
    <BreadcrumbLayout.Paper>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeDocumentTab}
          onChange={handleDocumentTabChange}
          aria-label="document tabs"
        >
          <Tab label="Active" value="Active" />
          <Tab label="Inactive" value="Inactive" />
        </Tabs>
      </Box>

      <Box display="flex" alignItems="end" justifyContent="end" padding={3} >
        {!showDocumentForm && !canEdit && (
          <Button
            startIcon={<Add />}
            variant="outlined"
                  disabled={isActive === false} 
            onClick={() => setShowDocumentForm(true)}
          >
            Document
          </Button>
        )}
      </Box>

      <Divider />

      <DocumentList
        documents={documents?.data}
        isBusy={loadingDocuments}
        totalDocuments={documents?.totalItems}
        perPage={perPage}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        handlePerRowsChange={handlePerRowsChange}
        onEditAction={handleEditDocument}
        isActive={isActive}
      />

      <FormDrawer
        title="Document"
        open={showDocumentForm}
        onClose={handleCancelDocument}
      >
        <DocumentForm
          isBusy={updatingDocument || addingDoc}
          editDocumentData={selectedDocument}
          onSubmit={(data) =>
            selectedDocument
              ? handleUpdateDocument(data)
              : handleAddDocument(data)
          }
          onCancel={handleCancelDocument}
        />
      </FormDrawer>
    </BreadcrumbLayout.Paper>
  );
};

export default DocumentsTab;